import type { PresetTone } from "./configPresets";
import type { DebugApp, DebugAppConfig, DebugConfigAdapter } from "./types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { CONFIG_FIELDS, presetKeysFor, presetMeta, presetsFor } from "./configPresets";

/**
 * Which environment the app runs with:
 * - `preset`: a preset exactly as defined (picked, or the default of this host when it equals a preset);
 * - `custom`: an override with own addresses, `basePreset` set when it started from a preset;
 * - `default`: no override and no preset matches the default addresses.
 */
export type DebugEnvironmentState
  = | { kind: "preset", preset: string, overridden: boolean }
    | { kind: "custom", basePreset: string | null }
    | { kind: "default" };

function readString(config: DebugAppConfig | null | undefined, key: string): string {
  const value = (config as Record<string, unknown> | null | undefined)?.[key];
  return typeof value === "string" ? value : "";
}

/** The form fields of a config, as strings. */
export function formValuesOf(app: DebugApp, config: DebugAppConfig | null | undefined): Record<string, string> {
  return Object.fromEntries(CONFIG_FIELDS[app].map(field => [field.key, readString(config, field.key)]));
}

function sameFormValues(app: DebugApp, a: DebugAppConfig | null | undefined, b: DebugAppConfig | null | undefined): boolean {
  return CONFIG_FIELDS[app].every(field => readString(a, field.key) === readString(b, field.key));
}

export function resolveEnvironmentState(app: DebugApp, adapter: Pick<DebugConfigAdapter, "config" | "overrideConfig">): DebugEnvironmentState {
  const presets = presetsFor(app);
  const override = adapter.overrideConfig.value;

  if (override) {
    const presetName = typeof override.presetName === "string" ? override.presetName : null;
    const preset = presetName ? presets[presetName] : undefined;
    if (presetName && preset) {
      return sameFormValues(app, override, preset)
        ? { kind: "preset", preset: presetName, overridden: true }
        : { kind: "custom", basePreset: presetName };
    }
    return { kind: "custom", basePreset: null };
  }

  const match = presetKeysFor(app).find(key => sameFormValues(app, adapter.config.value, presets[key]));
  return match ? { kind: "preset", preset: match, overridden: false } : { kind: "default" };
}

/** The environment of the app, its label and colour for the header, and the presets the Omgeving tab shows. */
export function useDebugEnvironment(app: DebugApp, adapter: DebugConfigAdapter) {
  const { t } = useI18n();

  const state = computed(() => resolveEnvironmentState(app, adapter));

  function presetLabel(key: string): string {
    const meta = presetMeta(key);
    if (meta.name) {
      return meta.name;
    }
    if (!meta.label) {
      throw new Error(`Debug preset ${key} has neither a name nor a label.`);
    }
    return t(meta.label);
  }

  function presetSubtitle(key: string): string {
    const meta = presetMeta(key);
    if (meta.host) {
      return meta.host;
    }
    if (!meta.subtitle) {
      throw new Error(`Debug preset ${key} has neither a host nor a subtitle.`);
    }
    return t(meta.subtitle);
  }

  const label = computed(() => {
    const current = state.value;
    if (current.kind === "preset") {
      return presetLabel(current.preset);
    }
    if (current.kind === "custom") {
      return current.basePreset
        ? t("debug_tools.environment.custom_based_on_short", { preset: presetLabel(current.basePreset) })
        : t("debug_tools.environment.custom");
    }
    return t("debug_tools.environment.default");
  });

  const tone = computed<PresetTone>(() => {
    const current = state.value;
    if (current.kind === "preset") {
      return presetMeta(current.preset).tone;
    }
    return current.kind === "custom" ? "info" : "gray";
  });

  return {
    state,
    label,
    tone,
    presetKeys: presetKeysFor(app),
    presets: presetsFor(app),
    presetLabel,
    presetSubtitle,
  };
}
